"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useState } from 'react';
import { useForm, FormProvider, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getAvailableForms,
  loadStartFormComponent,
  loadFormPreloadData,
  getDefaultValuesFromSchema,
} from '@/lib/form-utilities';
import { CamundaFormsService } from '@/lib/camunda/forms-service';
import type { ComponentType } from 'react';
import type { BaseFormSchema } from '@/types/zod-schema';
import type { StartFormProps } from '@/lib/form-utilities';

const AVAILABLE_FORMS = getAvailableForms();

export default function TestFormsPage() {
  const [selectedForm, setSelectedForm] = useState(() => {
    const requestedForm = new URLSearchParams(globalThis.location?.search ?? '').get('form');
    return requestedForm && AVAILABLE_FORMS.includes(requestedForm) ? requestedForm : '';
  });
  const [FormComponent, setFormComponent] = useState<ComponentType<StartFormProps> | null>(null);
  const [schema, setSchema] = useState<BaseFormSchema | null>(null);
  const [preloadedData, setPreloadedData] = useState<FieldValues | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const defaultValues = schema ? getDefaultValuesFromSchema(schema) : {};

  const form = useForm({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });
  const { reset: resetForm } = form;

  const handleFormChange = (formKey: string) => {
    setSelectedForm(formKey);
    const url = new URL(window.location.href);
    url.searchParams.set('form', formKey);
    window.history.replaceState(null, '', url.toString());
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!selectedForm) return;
      setLoading(true);
      setError(null);
      setFormComponent(null);
      setSchema(null);
      setPreloadedData(null);
      resetForm({});

      try {
        const [loadedComponent, loadedSchema, preloadResult] = await Promise.all([
          loadStartFormComponent(selectedForm),
          CamundaFormsService.loadFormSchema(selectedForm),
          loadFormPreloadData(selectedForm),
        ]);

        if (!mounted) return;

        if (!loadedComponent) {
          setError(`Formulario '${selectedForm}' no encontrado`);
        } else {
          setFormComponent(() => loadedComponent);
          if (loadedSchema) {
            setSchema(loadedSchema);
            const defaults = getDefaultValuesFromSchema(loadedSchema);
            const mergedValues = preloadResult.formValues
              ? { ...defaults, ...preloadResult.formValues }
              : defaults;
            resetForm(mergedValues);
          } else {
            resetForm(preloadResult.formValues ?? {});
          }
          setPreloadedData(preloadResult.data);
        }
      } catch (e) {
        console.error('Error cargando formulario de prueba:', e);
        if (mounted) setError(e instanceof Error ? e.message : 'Error cargando formulario');
      }
      if (mounted) setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [selectedForm, resetForm]);

  return (
    <div className="min-h-screen space-y-6 overflow-y-auto p-6 pb-16">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Test de Formularios</h1>
      </div>
      <Separator />

      <div className="max-w-xl">
        <label htmlFor="test-form-select" className="block text-sm font-medium mb-2">Selecciona un formulario</label>
        <Select value={selectedForm} onValueChange={handleFormChange}>
          <SelectTrigger id="test-form-select" className="w-full">
            <SelectValue placeholder="- Selecciona formulario -" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_FORMS.map((key) => (
              <SelectItem key={key} value={key}>{key}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <Spinner size={64} className="text-neutral-900 mx-auto mb-2" />
                <p>Cargando formulario...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center text-red-600">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && FormComponent && (
            <FormProvider {...form}>
              { }
              <FormComponent
                processDefinitionId="test-preview"
                variables={{}}
                preloadedData={preloadedData ?? undefined}
              />
            </FormProvider>
          )}

          {!loading && !error && !FormComponent && (
            <div className="text-muted-foreground text-center">
              Selecciona un formulario para visualizarlo aquí.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
